import React, { useCallback, useEffect, useState } from 'react';
import { X, Download, ArrowRight } from 'lucide-react';
export function openProspectusModal() {
  window.dispatchEvent(new CustomEvent('open-prospectus-modal'));
}
export function ProspectusModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
    setTimeout(() => {
      setIsSuccess(false);
      setName('');
      setEmail('');
    }, 400);
  }, []);
  useEffect(() => {
    window.addEventListener('open-prospectus-modal', handleOpen);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('open-prospectus-modal', handleOpen);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOpen, handleClose, isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      window.open('https://drive.google.com/file/d/1l16Gx6ljdv9GcI2ATLE3EPfwqsvR0ifo/view?usp=sharing', '_blank', 'noopener,noreferrer');
    }, 1200);
  };
  if (!isOpen && !isSubmitting && !isSuccess) return null;
  return <div className={`fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6 transition-all duration-500 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-181">
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-brand-charcoal/60 transition-all duration-500 ease-out ${isOpen ? 'backdrop-blur-md' : 'backdrop-blur-none'}`} onClick={handleClose} data-id="element-182" />

      {/* Modal Card */}
      <div className={`relative w-full max-w-4xl bg-brand-cream shadow-2xl overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-[0.97] translate-y-6 opacity-0'}`} onClick={e => e.stopPropagation()} data-id="element-183">
        {/* Close Button */}
        <button onClick={handleClose} className="absolute top-4 right-4 p-2.5 text-brand-brown hover:text-brand-red transition-colors rounded-full hover:bg-brand-shape/20 z-20" aria-label="Close modal" data-id="element-184">
          <X className="w-5 h-5" data-id="element-185" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[400px] md:min-h-[480px]" data-id="element-186">
          {/* Left — Text content */}
          <div className="flex flex-col justify-center p-8 sm:p-10 md:p-12 order-2 md:order-1" data-id="element-187">
            <div className="mb-2" data-id="element-188">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-gold" data-id="element-189">
                Firetree Fund
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-serif text-brand-charcoal mb-4" data-id="element-190">
              Download the Prospectus
            </h3>
            {isSuccess ? <div className="bg-brand-card/50 border border-brand-gold/30 p-6 text-center" data-id="element-191">
                <p className="text-brand-charcoal font-serif text-lg mb-1" data-id="element-192">
                  Thank you
                </p>
                <p className="text-brand-brown text-sm" data-id="element-193">
                  The prospectus should have opened in a new tab. If not,{' '}
                  <a href="https://drive.google.com/file/d/1l16Gx6ljdv9GcI2ATLE3EPfwqsvR0ifo/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-brand-red underline" data-id="element-194">
                    click here
                  </a>
                  .
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-195">
                <div data-id="element-196">
                  <label htmlFor="prospectus-name" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-197">
                    Full Name <span className="text-brand-red" data-id="element-198">*</span>
                  </label>
                  <input type="text" id="prospectus-name" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all placeholder:text-brand-brown/40" placeholder="Jane Doe" data-id="element-199" />
                </div>
                <div data-id="element-200">
                  <label htmlFor="prospectus-email" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-201">
                    Email Address <span className="text-brand-red" data-id="element-202">*</span>
                  </label>
                  <input type="email" id="prospectus-email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all placeholder:text-brand-brown/40" placeholder="jane@example.com" data-id="element-203" />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-brand-red text-white font-semibold hover:bg-brand-charcoal transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed group" data-id="element-204">
                  {isSubmitting ? 'Sending...' : <>
                      Download Prospectus
                      <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-205" />
                    </>}
                </button>
                <p className="text-xs text-brand-brown/60 text-center" data-id="element-206">
                  We respect your privacy. Your email will only be used to send
                  the prospectus.
                </p>
              </form>}
          </div>

          {/* Right — Image */}
          <div className="relative min-h-[240px] md:min-h-full order-1 md:order-2" data-id="element-207">
            <img src="https://cdn.magicpatterns.com/uploads/v5VgnsaJgHeo4yCzmgmhEh/MT_(8).jpg" alt="Firetree Fund Prospectus" className="absolute inset-0 w-full h-full object-cover" data-id="element-208" />
            <div className="absolute inset-0 bg-brand-red/20 mix-blend-multiply" data-id="element-209" />
          </div>
        </div>
      </div>
    </div>;
}